import { useState, useRef } from 'react';
import SEO from '../components/SEO';
import ReviewCarousel from '../components/ReviewCarousel';
import useFadeIn from '../hooks/useFadeIn';
import './Teaching.css';

const worlds = [
  {
    key: 'policy-quest',
    title: 'Policy Quest',
    subtitle: 'Introduction to Public Policy (Cornell)',
    preview: 'Learn policy by exploring it.',
    image: '/images/policy_quest.jpg',
    description:
      'Policy Quest places students inside the Galactic State System, where they explore how policy operates through roles, decisions, and unfolding institutional dynamics. Students engage with governance not as static rules, but as systems shaped by incentives, coordination, and uncertainty.',
  },
  {
    key: 'justice-junction',
    title: 'Justice Junction',
    subtitle: 'Race, Racism, and Public Policy (Cornell)',
    preview: 'Navigate through inequality, don\u2019t just study it.',
    image: '/images/justice-junction.jpg',
    description:
      'Justice Junction is an immersive city where students encounter how policy produces unequal outcomes across neighborhoods and institutions. Rather than analyzing inequality from a distance, students navigate through it.',
  },
  {
    key: 'world-of-care',
    title: 'The World of Care',
    subtitle: 'EARS Empathic Communication Training (Cornell)',
    preview: 'Learn how to care by practicing it.',
    image: '/images/world-of-care.jpg',
    description:
      'Care\u2019s World is an immersive emotional landscape designed to build empathy, communication, and self-awareness. Through metaphor, storytelling, and structured interaction, students develop the capacity to listen, respond, and support others.',
  },
];

const foundations = [
  {
    title: 'Holding Environments',
    description:
      'Learning requires spaces that are both structured and safe, where students can take risks without fear of harm.',
    sources: [
      { name: 'Donald Winnicott', work: 'Playing and Reality', url: 'https://search.worldcat.org/title/Playing-and-reality/oclc/19501982' },
      { name: 'Robert Kegan', work: 'In Over Our Heads', url: 'https://www.jstor.org/stable/j.ctv1pncpfb' },
    ],
  },
  {
    title: 'Learning Through Metaphor',
    description:
      'Students understand complex systems more deeply when they can engage symbolically, not just abstractly.',
    sources: [
      { name: 'George Lakoff & Mark Johnson', work: 'Metaphors We Live By', url: 'https://press.uchicago.edu/ucp/books/book/chicago/M/bo3637992.html' },
      { name: 'Jerome Bruner', work: 'Acts of Meaning', url: 'https://www.hup.harvard.edu/books/9780674003613' },
    ],
  },
  {
    title: 'Gamification & Engagement',
    description:
      'Structured play, narrative, and progression increase motivation, retention, and participation.',
    sources: [
      { name: 'Karl Kapp', work: 'The Gamification of Learning and Instruction', url: 'https://karlkapp.com/books/' },
      { name: 'Jane McGonigal', work: 'Reality Is Broken', url: 'https://legacy.iftf.org/our-work/featured-books/reality-is-broken/' },
    ],
  },
  {
    title: 'The Sociological Outsider',
    description:
      'Many students navigate institutions from positions of marginality; learning should make those dynamics visible and navigable.',
    sources: [
      { name: 'Patricia Hill Collins', work: 'Black Feminist Thought', url: 'https://en.wikipedia.org/wiki/Black_Feminist_Thought' },
      { name: 'W. E. B. Du Bois', work: 'The Souls of Black Folk', url: 'https://www.gutenberg.org/ebooks/408' },
    ],
  },
];

export default function Teaching() {
  const [activeCard, setActiveCard] = useState(null);
  const [showFoundations, setShowFoundations] = useState(false);
  const pageRef = useRef(null);
  useFadeIn(pageRef);

  const handleCardClick = (key) => {
    setActiveCard(prev => prev === key ? null : key);
  };

  return (
    <main id="main-content" ref={pageRef}>
      <SEO
        title="Teaching"
        description="Immersive, story-driven teaching environments by Elijah Moreno at Cornell University. Policy Quest, Justice Junction, and The World of Care."
        path="/teaching"
        image="https://elijahmoreno.com/images/policy_quest.jpg"
        imageWidth={1536}
        imageHeight={1024}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': worlds.map((w) => ({
            '@type': 'Course',
            name: w.title,
            description: w.description,
            provider: {
              '@type': 'CollegeOrUniversity',
              name: 'Cornell University',
              url: 'https://www.cornell.edu',
            },
            instructor: {
              '@type': 'Person',
              name: 'Elijah S. Moreno',
              url: 'https://elijahmoreno.com/about',
            },
          })),
        }}
      />

      <section className="section section--first teaching__hero-section">
        <div className="container">
          <h1 className="section-title teaching__title fade-in">My Approach to Teaching</h1>
          <div className="teaching__intro fade-in fade-in--delay-1">
            <p>
              I design teaching environments where students experience policy instead of only reading about it. Across my courses, I build immersive, story-driven worlds that put students inside the systems we study. They take part in those systems rather than watching from a distance, working through governance, inequality, and human interaction as those forces actually unfold.
            </p>
            <p>
              These worlds are more than traditional simulations. They are grounded in research from economics, sociology, psychology, education, and neuroscience, and they use metaphor, narrative, and interaction to make complicated ideas clearer and hard conversations possible.
            </p>
            <p>
              At the center is a straightforward commitment: be kind, be creative, and be someone who cares. I treat empathy, communication, and the ability to engage across difference as core competencies rather than soft skills. If I can get students to care about Orbit the Explorer, the streets of Justice Junction, or Care the Koala Bear, I can get them to care about anything.
            </p>
            <p>
              These are distinct experiences, but they share a common design language and reinforce one another, shaping how students think, feel, and act.
            </p>
          </div>
          <p className="teaching__worlds-subtitle fade-in fade-in--delay-2">
            Click on a world to learn more.
          </p>
          <div className="teaching__worlds-grid fade-in fade-in--delay-3">
            {worlds.map((world) => (
              <div
                key={world.key}
                className={`teaching__card ${activeCard === world.key ? 'teaching__card--active' : ''}`}
                onClick={() => handleCardClick(world.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(world.key);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-expanded={activeCard === world.key}
                aria-label={world.title}
              >
                <div className="teaching__card-front">
                  <img
                    src={world.image}
                    alt={world.title}
                    className="teaching__card-img"
                    width={600}
                    height={400}
                  />
                </div>
                <div className="teaching__card-back">
                  <h3 className="teaching__card-back-title">{world.title}</h3>
                  <p className="teaching__card-back-subtitle">{world.subtitle}</p>
                  <p className="teaching__card-back-desc">{world.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container--narrow">
          <h2 className="section-title teaching__section-heading fade-in">Does My Approach Work?</h2>
          <div className="fade-in fade-in--delay-1">
            <ReviewCarousel />
          </div>
          <p className="teaching__reviews-source fade-in fade-in--delay-2">
            These are real student responses sourced from official Cornell TA course evaluations for
            {' '}<em>Public Policy for an Aging Society</em> (2024FA), <em>Introduction to Public Policy</em> (2025SP, 2026SP), and <em>Race, Racism, and Public Policy</em> (2025FA),
            {' '}from the question: &ldquo;What were the strengths of this student in their capacity as a teaching assistant?&rdquo;
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container--narrow">
          <button
            className="teaching__accordion-header fade-in"
            onClick={() => setShowFoundations(!showFoundations)}
            aria-expanded={showFoundations}
          >
            <h2 className="teaching__accordion-title">If you still aren't convinced, here's the evidence base for my approach</h2>
            <span className="teaching__accordion-chevron" aria-hidden="true">
              {showFoundations ? '\u2212' : '+'}
            </span>
          </button>
          {showFoundations && (
            <div className="teaching__foundations">
              {foundations.map((f) => (
                <div key={f.title} className="teaching__foundation-card">
                  <h3 className="teaching__foundation-title">{f.title}</h3>
                  <p className="teaching__foundation-desc">{f.description}</p>
                  <p className="teaching__foundation-sources">
                    Key sources:{' '}
                    {f.sources.map((s, i) => (
                      <span key={s.name}>
                        {i > 0 && '; '}
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="teaching__source-link">
                          {s.name}, <em>{s.work}</em>
                        </a>
                      </span>
                    ))}
                  </p>
                </div>
              ))}

              <p className="teaching__evidence-note">
                I draw on over 100 peer-reviewed academic journal articles that inform my approach. The ones listed here are what I consider the core foundations everything is built on.
              </p>

              <div className="teaching__classroom-approach">
                <h3 className="teaching__foundation-title">Shared Classroom Approach</h3>
                <p className="teaching__foundation-desc">
                  Across all three environments, I use structured protocols:
                </p>
                <ul className="teaching__protocols">
                  <li>Listen to understand, not to respond</li>
                  <li>Stay curious, especially in disagreement</li>
                  <li>Check your assumptions</li>
                  <li>Recognize that policy is personal</li>
                </ul>
                <p className="teaching__foundation-desc">
                  These protocols are built into the learning design itself.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

    </main>
  );
}
